'use client';

import classnames from 'classnames';

import { useStableValue } from '../chart-data';
import {
  seriesColor,
  toLegendLabels,
  tooltipOptions,
  useChartTheme,
  xAxisOptions,
  yAxisOptions,
} from '../chart-theme';
import { Legend } from '../layout/legend/Legend';
import { useChart } from '../useChart';
import type { LineChartProps } from './LineChart.types';

import styles from './LineChart.module.scss';

function LineChart({
  style,
  className,
  labels: labelsProp,
  dataset: datasetProp,
  label,
  testId = 'ui-line-chart',
}: LineChartProps) {
  const theme = useChartTheme();
  const dataset = useStableValue(datasetProp);
  const labels = useStableValue(labelsProp);

  const canvasRef = useChart<'line'>(
    () => ({
      type: 'line',
      data: {
        labels: labels,
        datasets: dataset.map((data, index) => {
          const color = seriesColor(data.color, index);
          return {
            data: data.values,
            label: data.label,
            borderColor: color,
            fill: false,
            borderWidth: 4,
            pointBorderColor: color,
            pointBackgroundColor: theme.background,
            pointBorderWidth: 4,
          };
        }),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: false,
          },
          legend: {
            display: false,
          },
          tooltip: tooltipOptions(theme),
        },
        scales: {
          x: xAxisOptions(theme),
          y: yAxisOptions(theme),
        },
        elements: {
          line: {
            tension: 0.4,
          },
        },
      },
    }),
    [dataset, labels, theme],
  );

  return (
    <div
      style={style}
      className={classnames(styles['line-chart'], className)}
      data-testid={testId}
    >
      <div className={styles['kpi']}>
        <canvas ref={canvasRef} role="img" aria-label={label} />
      </div>
      <Legend labels={toLegendLabels(dataset)} />
    </div>
  );
}

export { LineChart };
